import axios from 'axios';
import { find, forEach, forOwn, get, map } from 'lodash';
import Mexp from 'math-expression-evaluator';
import memoize from 'memoize-one';

import SERVICES from '../enums/IntegrationServices';
import SectionType from '../enums/SectionType';
import { States } from '../enums/USStates';
import DateFormatter from '../utils/DateFormatter';
import { format, getValueAsNumber, transform } from '../utils/ValueTypeFormatter';
import CalculationError, { CALC_ERRORS } from './CalculationError';
import { PARTY_PROPERTIES } from './Party';
import TableColumn from './TableColumn';

export const VariableType = {
  SIMPLE: '#',
  PARTY: '@',
  CALCULATED: '%',
  REF: '$',
  TERM: '!',
  PROTECTED: '*',
  CONNECTED: '+',
  FOOTNOTE: '^',
  PAGE_COUNT: '~',
};

// prettier-ignore
export const VariableKey = {
  '!': 'TERM',
  '#': 'SIMPLE',
  '@': 'PARTY',
  '%': 'CALCULATED',
  '$': 'REF',
  '*': 'PROTECTED',
  '+': 'CONNECTED',
  '^': 'FOOTNOTE',
  '~': 'PAGE_COUNT',
};

export const ValueType = {
  STRING: 'string',
  LIST: 'list',
  DATE: 'date',
  STATE: 'state',
  NUMBER: 'number',
  PERCENT: 'percent',
  CURRENCY: 'currency',
  IMAGE: 'image',
  TABLE: 'table',
  MULTI_SELECT: 'multi-select',
  // These are FV-specific; hide from UI
  CONTACT: 'contact',
  PROJECT: 'project',
  ADDRESS: 'address',
};

export const SimpleTypeLabels = [
  { data: ValueType.STRING, label: 'Text', column: true },
  { data: ValueType.DATE, label: 'Date', column: true },
  { data: ValueType.NUMBER, label: 'Number', column: true },
  { data: ValueType.CURRENCY, label: 'Currency', column: true },
  { data: ValueType.PERCENT, label: 'Percentage', column: true },
  { data: ValueType.LIST, label: 'List' },
  { data: ValueType.MULTI_SELECT, label: 'Multi-Select' },
  { data: ValueType.STATE, label: 'U.S. State' },
  { data: ValueType.IMAGE, label: 'Image' },
  { data: ValueType.TABLE, label: 'Table' },
  { data: ValueType.CONTACT, label: 'Contact', column: true },
];

export const SEPERATORS = [', ', '&', '/', '-', '&&', '//', '_', '+'];

export const ConditionalValueTypes = [
  ValueType.STRING,
  ValueType.DATE,
  ValueType.STATE,
  ValueType.NUMBER,
  ValueType.PERCENT,
  ValueType.CURRENCY,
  ValueType.LIST,
];

export const ValueTypeValidations = {
  [ValueType.NUMBER]: (value) => /^(-|[0-9]|\.|,)*$/.test(value),
  [ValueType.PERCENT]: (value) => /^(-|[0-9]|\.|,)*$/.test(value),
  [ValueType.CURRENCY]: (value) => /^(-|[0-9]|\.|,)*$/.test(value),
};

export const StringValueTypes = [
  ValueType.STRING,
  ValueType.LIST,
  ValueType.STATE,
  ValueType.IMAGE,
  ValueType.MULTI_SELECT,
];
export const NumericValueTypes = [ValueType.NUMBER, ValueType.PERCENT, ValueType.CURRENCY];

export const isNumeric = (type) => NumericValueTypes.includes(type);

export const ELEMENT_TYPES = [
  {
    type: VariableType.SIMPLE,
    class: 'var',
    title: 'Variable',
    plural: 'Variables',
    icon: 'fieldsEdit',
    tip: 'Variables are tokens that get filled in for each instance of this contract',
  },
  {
    type: VariableType.CALCULATED,
    class: 'calc',
    title: 'Calculated Variable',
    plural: 'Calculated Variables',
    icon: 'calculator',
    tip: 'Calculated Variables reference other variables and are populated dynamically',
  },
  {
    type: VariableType.PARTY,
    class: 'party',
    title: 'Party',
    plural: 'Parties',
    icon: 'users',
    tip: 'Parties will each get an auto-generated signature area in the Signature block of your contract',
  },
  {
    type: VariableType.PROTECTED,
    class: 'secret',
    title: 'Secret',
    plural: 'Secrets',
    icon: 'fieldsSecret',
    tip: 'Secrets are only viewable by authorized users on Outlaw and will not be included in PDF export',
  },
  {
    type: VariableType.REF,
    class: 'ref',
    title: 'Reference',
    plural: 'References',
    icon: 'ref',
    tip: 'References are smart links whose text will auto-update if the target section number changes',
  },
  {
    type: VariableType.CONNECTED,
    class: 'connect',
    title: 'Connected',
    plural: 'Connected',
    icon: 'connect',
    tip: 'Connected variables are pieces of data that come from and sync with other other systems',
  },
  {
    type: VariableType.FOOTNOTE,
    class: 'footnote',
    title: 'Footnote',
    plural: 'Footnotes',
    icon: 'versions',
    tip: 'Footnote',
  },
  {
    type: VariableType.PAGE_COUNT,
    class: 'page-count',
    title: 'Page Count',
    plural: 'Page Counts',
    icon: 'numbering',
    tip: 'Add page count to footers.',
  },
];

// Enumerate a generic set of object types for connected variables that 3rd party data sources can map to
// Not every data source will support all types
export const EXTERNAL_TYPES = {
  FIELD: 'field',
  PROJECT: 'project',
  CONTACT_LIST: 'contactList',
  CONTACT: 'contact',
  COLLECTION: 'collection',
  COLLECTION_FIELD: 'collectionField',
  MERGE_CODE: 'mergeCode',
};

export const CONNECTION_TYPES = [
  {
    key: 'none',
    title: 'Once',
    autoPull: false,
    autoPush: false,
    description: 'Populate initial value from remote',
  },
  {
    key: 'pull',
    title: 'Pull',
    autoPull: true,
    autoPush: false,
    description: 'Sync remote updates to Outlaw',
  },
  {
    key: 'push',
    title: 'Push',
    autoPull: false,
    autoPush: true,
    description: 'Sync Outlaw updates to remote',
  },
  {
    key: 'both',
    title: 'Bidirectional',
    autoPull: true,
    autoPush: true,
    description: 'Sync updates in both directions',
  },
];

// all savable variable properties for all variables EXCEPT Connected variables.
export const SAVEABLE_VAR_FIELDS = [
  'type',
  'name',
  'displayName',
  'value',
  'prompt',
  'valueType',
  'multiline',
  'multilineValueOptions',
  'multilineValueLabels',
  'showTotalRow',
  'totalRowLabel',
  'formula',
  'variants',
  'seperator',
];

// Explicit list of Variable properties necessary to properly define a Connected variable
// see commitVariableSuggestion in ContentSection/SectionEditor
export const CONNECTED_VAR_FIELDS = [
  'name',
  'displayName',
  'type',
  'valueType',
  'prompt',
  'connectType',
  'externalType',
  'externalSelector',
  'writable',
  'multiline',
  'multilineValueOptions',
  'multilineValueLabels',
  'autoPull',
  'autoPush',
  'projectLinkFieldType',
];

//These are all of the fields that should always be in sync
export const CONNECTED_VAR_COMPARISON_FIELDS = [
  'name',
  'type',
  'valueType',
  'prompt',
  'connectType',
  'externalType',
  'externalSelector',
  'writable',
];

export const NUMBER_PROPERTIES = [
  {
    data: 'none',
    label: 'Numeric display (default)',
    description: 'Format with separators, e.g., "10,000"',
  },
  {
    data: 'spelled',
    label: 'Prose display',
    description: 'Spell out in prose, e.g., "ten thousand"',
  },
];

export const TEXT_TRANSFORMS = [
  {
    data: 'none',
    label: 'No text transform (default)',
    description: 'default',
  },
  {
    data: 'uppercase',
    label: 'Uppercase',
    description: 'Text will be transformed to UPPERCASE',
  },
  {
    data: 'lowercase',
    label: 'Lowercase',
    description: 'Text will be transformed to lowercase',
  },
];

// Use today's date for examples!
const today = new Date();

export const DATE_PROPERTIES = [
  {
    data: 'none',
    label: 'Standard prose (default)',
    description: `e.g., "${DateFormatter.date(today)}"`,
  },
  {
    data: 'verbose',
    label: 'Verbose prose',
    description: `e.g., "${DateFormatter.verbose(today)}"`,
  },
  {
    data: 'raw',
    label: 'Unformatted',
    description: `Show text exactly as entered`,
  },
  {
    data: 'day',
    label: 'Day of month',
    description: `e.g., "${DateFormatter.day(today)}"`,
  },
  {
    data: 'dayOrdinal',
    label: 'Day of month - ordinal',
    description: `e.g., "${DateFormatter.dayOrdinal(today)}"`,
  },
  {
    data: 'dayOfWeek',
    label: 'Day of week',
    description: `e.g., "${DateFormatter.dayOfWeek(today)}"`,
  },
  {
    data: 'month',
    label: 'Month',
    description: `e.g., "${DateFormatter.month(today)}"`,
  },
  {
    data: 'year',
    label: 'Year',
    description: `e.g., "${DateFormatter.year(today)}"`,
  },
];

export const LOCALIZED_DATE_PROPERTIES = [
  {
    data: 'en-US',
  },
  {
    data: 'en-CA',
  },
  {
    data: 'en-AU',
  },
  {
    data: 'en-UK',
  },
  {
    data: 'en-MX',
  },
];

export const EXTRACT_TYPE = {
  STRUCTURED: 'structured',
  FREEFORM: 'freeform',
}

// Predefined variable definition for templates that are enabled with batch operations
export const BATCH_ID_VAR = {
  name: 'Batch-ID',
  displayName: 'Batch ID',
  type: VariableType.SIMPLE,
  valueType: ValueType.STRING,
};

export const EMPTY_SEARCH_VALUE = '--';

export const rxCalculableVariables = /\[[#%+]([-_\w\d.]+)\]/gim;

export const validateFormula = (formula) => {
  const rxCalc = new RegExp(rxCalculableVariables, 'gim');

  // First strip all vars out of formula
  formula = formula.replace(rxCalc, '');

  // Remaining text should only be simple mathematical characters and whitespace
  formula = formula.replace(/[*/\-+\s.\d()^]/gi, '');

  // If there's anything left, it's invalid
  return formula === '';
};

// Ensure that variable names do not have spaces in them
export const sanitize = (name) => name.replace(/[^_-\w\d]/g, '-');

export const discoverValueType = (val) => {
  // Try to parse the raw value as a series of different native types
  // If any of them work, return that; otherwise just default to string

  // Dates are tricky because short numbers, e.g., "12" still parse successfully as dates
  // So we set a minimum length of 5 (i.e., a single digit for each of m/d/y plus a separator)
  let date = Date.parse(val);
  if (!isNaN(date) && val.length >= 5) {
    return ValueType.DATE;
  }

  let number = typeof val === 'string' ? val : val.toString();
  number = Number.parseFloat(val.replace(/[$,%]/g, ''));
  if (!isNaN(number)) {
    //TODO: percentage and currency
    return ValueType.NUMBER;
  }

  return ValueType.STRING;
};

export const imageSrc = (value) => {
  //legacy base64 saved variable
  if (typeof value === 'string') {
    return value;
  } else {
    //if the new format {attachmentSrc, attachmentID}
    return value ? value.downloadURL : null;
  }
};

//with the change to image formatting we need to convert the downloadURL
//to a readable base64 string for pdfkit and docx libraries to generate images with.
export const imageExportBuffer = async (deal) => {
  const promises = [];
  const variableImages = {};

  forEach(deal.variables, (variable) => {
    if (variable.valueType === ValueType.IMAGE && variable.value) {
      promises.push(
        new Promise(async (resolve) => {
          let buffer;
          if (typeof variable.value === 'object') {
            const attachment = find(deal.attachments, { key: variable.value.key });
            if (attachment) {
              const image = await axios.get(variable.value.downloadURL, { responseType: 'arraybuffer' });
              const base64 = Buffer.from(image.data).toString('base64');
              buffer = `data:image/${attachment.extension};base64,${base64}`;
              variableImages[variable.value?.key] = buffer;
            }
          }
          resolve();
        })
      );
    }
  });

  await Promise.all(promises);
  return variableImages;
};

export default class Variable {
  name = null;
  type = null;
  value = null;
  prompt = null;
  displayName = null;
  pluralName = null;
  assigned = null;
  multiline = null;
  multilineValueOptions = null;
  multilineValueLabels = [];
  //multiselect seperator
  seperator = ', ';

  _columns = null;
  _valueType = null;

  // for CONNECTED variables
  connectType = null; //datasource, e.g., 'filevine'
  externalType = null; //object type on external system; differs for each data source
  externalSelector = null; //path on external system; syntax may differ for each source, but should contain sufficient context for API calls
  autoPull = false; //whether to fetch/syncing external data on every Deal load
  autoPush = false; //whether to push updates to external system when updated on Outlaw
  writable = false; //whether updates on Outlaw *can* be pushed to external data source (required for autoPush to be enabled)
  projectLinkFieldType = null; //field type of the projectLink to differentiate CollectionField projectLink and simple form field projectLink

  //for Calculated variables
  formula = null;

  // Special field that is only (currently) used for Filevine contacts, so that we know which object to target for property updates
  externalObjectID = null;

  showTotalRow = false;
  totalRowLabel = null;
  //variants is used for pdf data extraction.
  variants = [];
  extract = false;
  extractType = null;
  extractionInstructions = '';

  constructor({ type, name, displayName = null, pluralName = null, deal, connectType, valueType = null }) {
    this.type = type;
    this.valueType = valueType;
    this.name = name;
    this.displayName = displayName;
    this.pluralName = pluralName;
    this.deal = deal;
    this.connectType = connectType;

    if ([VariableType.SIMPLE, VariableType.CONNECTED].includes(type) && !this.valueType) {
      this.valueType = ValueType.STRING;
    }
  }

  get textTransform() {
    const varName = this.name.toString();
    const [, ...subPropAndTransform] = varName.split('.');
    const transfromObject = TEXT_TRANSFORMS.find(({ data }) => subPropAndTransform.includes(data));
    if (!transfromObject) {
      return TEXT_TRANSFORMS.find(({ data }) => 'none' === data);
    }
    return transfromObject;
  }

  get hasAttachment() {
    return this.valueType === ValueType.IMAGE && get(this.value, 'downloadURL');
  }

  get val() {
    var v = null;
    const varName = this.name.toString();
    const [baseVarName, ...subPropAndTransform] = varName.split('.');
    const baseVariable = this.baseVariable;

    let party = null;

    const { subProp, textTransform } = [...subPropAndTransform].reduce(
      (acc, txt) => {
        if (TEXT_TRANSFORMS.find(({ data }) => txt === data)) {
          return {
            ...acc,
            textTransform: txt,
          };
        }
        if (
          [...DATE_PROPERTIES, ...NUMBER_PROPERTIES, ...PARTY_PROPERTIES, ...LOCALIZED_DATE_PROPERTIES].find(
            ({ data }) => txt === data
          )
        ) {
          return {
            ...acc,
            subProp: txt,
          };
        }
        return acc;
      },
      {
        subProp: null,
        textTransform: null,
      }
    );

    switch (this.type) {
      // Simple variables are basic strings unless there's a "derived" property requested
      case VariableType.SIMPLE:
      case VariableType.CONNECTED:
        // Special case for table totals, whose (base table) variable type can be either SIMPLE or CONNECTED
        // follow same error pattern as for CALCULATED below because these variables behave very similarly
        if (this.isTableTotal) {
          try {
            const col = this.relatedTotalColumn;
            return format(col.total, col.valueType);
          } catch (err) {
            return err.errorValue;
          }
        }

        v = this.simpleVal;
        if (baseVariable !== this) {
          if (subProp && !textTransform) {
            return format(baseVariable.value, baseVariable.valueType, subProp);
          }
          if (textTransform) {
            if (subProp) {
              const formatted = format(baseVariable.value, baseVariable.valueType, subProp);
              return transform(formatted, textTransform);
            } else {
              return transform(baseVariable.simpleVal, textTransform);
            }
          }
        }
        break;
      //term variables are pre-defined in deal template
      //so from an evaluation perspective they work exactly like simple variables (just lookup the value)
      case VariableType.TERM:
        v = this.simpleVal;
        break;
      case VariableType.CALCULATED:
        try {
          const calculated = this.calculate(this.formula);
          return format(calculated.result, calculated.valueType);
        } catch (err) {
          v = err.errorValue;
        }
        break;
      case VariableType.PROTECTED:
        if (this.value != null) {
          //v = '********';
          v = '••••••••••';
        }
        break;
      //party variables are pointing to a party
      //find appropriate user (if present) and use that user object
      case VariableType.PARTY:
        //use displayName property as default party variable if no field is provided
        party = this.deal.getPartyByID(baseVarName);

        if (party) {
          if (subPropAndTransform[1]) {
            if (subPropAndTransform[0] === 'address') {
              v = party.get('addressProperties', true);
              if (v) v = v.getSubPropValue(subPropAndTransform[1]);
            } else {
              v = party.get(subProp || 'displayName', true)[textTransform];
            }
          } else {
            v = party.get(subProp || 'displayName', true);
          }

          if (v == '') v = null;
        }
        break;
      //references let us tie text like "Section 3" to an actual Section object (via id)
      //so that they will update if structure changes
      case VariableType.REF:
        if (this.value) {
          //if there's a value, it should be an id to another section
          const sec = this.deal.sections[this.value];
          if (sec)
            switch (sec.sectiontype) {
              case SectionType.SOURCE:
                //legacy support when var name included prefix, e.g., [$Section 1a]'
                //now names are auto-generated keys so will not have spaces
                var pieces = this.name.split(' ');
                if (pieces.length > 1) {
                  const pre = pieces.length > 1 ? pieces[0] + ' ' : '';
                  v = pre + sec.sourceNumber(true);
                } else {
                  //otherwise use the displayName property as a prefix
                  v = this.displayName ? `${this.displayName} ${sec.sourceNumber(true)}` : sec.sourceNumber(true);
                }

                //shave off trailing . for display
                if (v.endsWith('.')) v = v.substring(0, v.length - 1);
                break;

              case SectionType.APPENDIX:
                v = sec.displayname;
                break;
              default:
                break;
            }
        }
        break;
      case VariableType.FOOTNOTE:
        v = this.value;
        break;
    }
    return v;
  }

  get simpleVal() {
    switch (this.valueType) {
      case ValueType.IMAGE:
        return imageSrc(this.value);
      case ValueType.LIST:
        return this.selectedOptionTitle;
      case ValueType.STATE:
        return States[this.value] || this.value;
      case ValueType.TABLE:
        return this.tableValueFormatted;
      default:
        return format(this.value, this.valueType);
    }
  }

  get isDerived() {
    return [VariableType.SIMPLE, VariableType.CONNECTED].includes(this.type) && this.name.split('.').length > 1;
  }

  // "Derived" variables are sub-properties of other variables, e.g., [#Date.month] or [#Cost.spelled]
  // These are technically managed as separate Variable instances
  // and they will have unique names/keys in Deal.variables and Section.variables collections
  // but need a quick way to refer back to their underlying variable definitions
  get baseVariable() {
    if (!this.isDerived) return this;
    const baseName = this.name.split('.')[0];
    // If (somehow) the underlying Variable isn't found, fallback to self so that this getter can always be used safely
    return this.deal.variables[baseName] || this;
  }

  get connection() {
    if (!this.connectType) return null;
    return find(this.deal.connections, { type: this.connectType });
  }

  get service() {
    if (!this.connectType || this.type !== VariableType.CONNECTED) return null;
    return find(SERVICES, { key: this.connectType });
  }

  get isConnected() {
    return !!this.service;
  }

  get isControlled() {
    return this.service && this.autoPull && !this.autoPush;
  }

  get isTableTotal() {
    return this.isDerived && this.baseVariable.valueType === ValueType.TABLE;
  }

  get isNumeric() {
    return (
      this.type === VariableType.SIMPLE &&
      [ValueType.CURRENCY, ValueType.NUMBER, ValueType.PERCENT].includes(this.valueType)
    );
  }

  get hasValidation() {
    return this.type === VariableType.SIMPLE && ValueTypeValidations[this.valueType];
  }

  validateValue(value) {
    if (!ValueTypeValidations[this.valueType]) return true;
    return ValueTypeValidations[this.valueType](value);
  }

  // This is a recursive function for calculated variables
  // It needs to be recursive so that existing stack can be passed through to catch circular references (see first error)
  calculate(formula, stack = [], simpleStack = []) {
    if (stack.includes(this.name)) {
      throw new CalculationError(CALC_ERRORS.CIRCULAR);
    }

    stack.push(this.name);

    let valueType = ValueType.NUMBER;
    let result, matchVar;
    let replacedFormula = formula;
    const rxCalc = new RegExp(rxCalculableVariables, 'gim');

    // Find each var referenced in the formula so that we can replace with underlying values
    while ((matchVar = rxCalc.exec(formula)) !== null) {
      let val, col;
      const varName = matchVar[1];
      const targetVar = this.deal.variables[varName];

      // First we need to catch a special edge case where we've referenced a column total,
      // e.g., [#SimpleTable.total] or [+ConnectedTable.total]
      // If that total column is not referenced anywhere else in the doc, then targetVar will not be found
      const [tableName, colID] = varName.split('.');
      const table = this.deal.variables[tableName];
      if (table?.valueType === ValueType.TABLE && colID) {
        col = find(table.columns, { id: colID });
      }

      // Make sure every referenced var actually exists
      if (!targetVar && !col) {
        throw new CalculationError(CALC_ERRORS.REF);
      }

      // Now we can check for the special case above first -- if it's a column, populate its total value
      if (col) {
        val = col.total;
        valueType = col.valueType;
      }
      // Recurse (and pass in current stack) when other calculated vars are used in formula
      else if (targetVar.type === VariableType.CALCULATED) {
        const calculated = targetVar.calculate(targetVar.formula, [...stack], [...simpleStack]);
        val = calculated.result;
        valueType = calculated.valueType;
      } else {
        // Microsoft Excel and Google Sheets both handle missing/empty referenced cells as 0 (instead of an error), so we'll follow that convention
        if (!targetVar.val) {
          val = 0;
        } else {
          val = getValueAsNumber(String(targetVar.value));
        }

        // Simple vars can be referenced multiple times and by other calulated vars in the formula without creating circular ref errors,
        // But we still want to keep track of these separately so that we can infer value type (e.g., handle as currency) below
        simpleStack.push(targetVar.name);
      }

      // Percent vars get a "%" added when rendered, but they retain their values, e.g., for 20% the value is "20"
      // so for calculations, we need to divide by 100 to handle as a true percent
      if (col?.valueType === ValueType.PERCENT || targetVar?.valueType === ValueType.PERCENT) {
        val /= 100;
      }

      // Now we've gathered values for all the referenced variables, so replace the references with the values
      replacedFormula = replacedFormula.replace(matchVar[0], val);
    }

    try {
      // Finally, we should have a purely mathematical formula; use math-expression-evaluator to eval
      // https://www.npmjs.com/package/math-expression-evaluator
      // This lib actually parses the expression so we avoid all security issues and pitfalls with using eval() or Function()
      result = new Mexp().eval(replacedFormula);
    } catch (err) {
      throw new CalculationError(CALC_ERRORS.FORMULA);
    }
    if (result === Infinity) {
      throw new CalculationError(CALC_ERRORS.DIV0);
    }

    // If any of the source vars are currency, persist formatting to the calculated var
    if (find(simpleStack, (vName) => this.deal.variables[vName].valueType === ValueType.CURRENCY)) {
      valueType = ValueType.CURRENCY;
    }

    return { result, valueType };
  }

  // First, find target column. (baseVariable is the table, so that has already been found if we get here.
  // Use ref error for both if either one is not found
  get relatedTotalColumn() {
    const colID = this.name.split('.').pop();
    const col = find(this.baseVariable.columns, { id: colID });
    if (!col) {
      throw new CalculationError(CALC_ERRORS.REF);
    }
    if (col.total === undefined) {
      throw new CalculationError(CALC_ERRORS.VALUE);
    }
    return col;
  }

  // Attempt to convert to a more native value (i.e., not a string)
  // For indexing and numeric variable filtering
  get searchVal() {
    // Empty values need a consistent value in search index so that we can filter on them
    // this supports the KNOWN and UNKNOWN filter Operators
    if (!this.value) return EMPTY_SEARCH_VALUE;

    switch (this.valueType) {
      case ValueType.NUMBER:
      case ValueType.PERCENT:
      case ValueType.CURRENCY:
        // If we can parse numeric values successfully, return that;
        // otherwise just return raw value
        const num = Number.parseFloat(this.value.toString().replace(/[$,%]/g, ''));
        if (!isNaN(num)) {
          return num;
        }
        return this.value;
      case ValueType.DATE:
        // See extra check above as dates are tricky
        // If it parses successfully we want the date in seconds (not ms)
        if (this.value && discoverValueType(this.value) === ValueType.DATE) {
          return Date.parse(this.value) / 1000;
        }
        return this.value;
      case ValueType.STRING:
      case ValueType.LIST:
      case ValueType.STATE:
      default:
        return this.value;
    }
  }

  get rawText() {
    return `[${this.type}${this.name}]`;
  }

  get options() {
    return this.memoOptions(this.valueType, this.prompt);
  }

  memoOptions = memoize((valueType, prompt) => {
    if (valueType == ValueType.LIST && prompt) {
      const raw = prompt.split('\n');
      const opts = [];
      raw.map((item) => {
        if (!item) return;

        var pieces = item.split('|');
        opts.push({
          key: pieces.length > 1 ? pieces[0] : item,
          title: pieces.length > 1 ? pieces[1] : item,
        });
      });
      return opts;
    } else if (valueType == ValueType.MULTI_SELECT && prompt) {
      const raw = prompt.split('\n');
      const opts = [];
      raw.map((item) => {
        if (!item) return;

        var pieces = item.split('|');
        opts.push({
          value: pieces.length > 1 ? pieces[0] : item,
          label: pieces.length > 1 ? pieces[1] : item,
        });
      });
      return opts;
    } else {
      return [];
    }
  });

  get columnsToTotal() {
    return this.columns.filter((col) => NumericValueTypes.includes(col.valueType) && col.totalColumn);
  }

  get totalEligibleColumns() {
    return this.columns.filter((col) => NumericValueTypes.includes(col.valueType));
  }

  get columns() {
    if (this.valueType === ValueType.TABLE) {
      if (this._columns) {
        return this._columns;
      } else if (this.prompt) {
        const raw = this.prompt.split('\n');
        const cols = [];
        raw.map((line) => {
          line = line.trim();
          if (line) {
            cols.push(
              new TableColumn(
                {
                  id: line,
                  displayName: line,
                  valueType: ValueType.STRING,
                  editable: true,
                },
                this
              )
            );
          }
        });
        return cols;
      }
    }

    return [];
  }

  set columns(columns) {
    this._columns = columns;
  }

  get selectedOptionTitle() {
    if (!this.value) return null;
    const opt = find(this.options, { key: this.value });
    return opt ? opt.title : null;
  }

  get multiSelectedOptions() {
    if (!this.value) return [];
    return this.memoOptions(this.valueType, this.value);
  }

  get displayType() {
    var t = null;
    forOwn(VariableType, (val, key) => {
      if (val == this.type) t = key;
    });
    return t ? t.toLowerCase() : null;
  }

  //reverse lookup all sections that include references to this variable
  //or base name (not property-specific) of this variable
  get locations() {
    const sections = [];
    const baseName = this.name.split('.')[0];
    forEach(this.deal.sections, (sec) => {
      if (find(sec.variables, (v, name) => name.split('.')[0] == baseName)) sections.push(sec);
    });
    return sections;
  }

  get isInferred() {
    return get(this.deal, `raw.variables[${this.name}]`, null) === null;
  }

  get isUnused() {
    // List vars may be used for conditionals in other sections
    // without explicitly being referenced... so they should still show as being in use
    if (this.valueType === ValueType.LIST) {
      const conditional = find(this.deal.sections, (sec) => !!find(sec.conditions, { variable: this.name }));
      return !conditional && !this.locations.length;
    } else {
      return !this.locations.length;
    }
  }

  get isConditional() {
    return ConditionalValueTypes.includes(this.valueType);
  }

  getPropertyAndTextTransform(pieces) {
    if (pieces.length) {
      return pieces.reduce(
        (acc, piece, index) => {
          if (index < 1) {
            return acc;
          }
          if (TEXT_TRANSFORMS.find(({ data }) => piece === data)) {
            return {
              ...acc,
              textTransform: piece,
            };
          }
          if ([...DATE_PROPERTIES, ...NUMBER_PROPERTIES, ...PARTY_PROPERTIES].find(({ data }) => piece === data)) {
            return {
              ...acc,
              property: piece,
            };
          }
          return acc;
        },
        {
          textTransform: 'none',
          property: 'none',
        }
      );
    }
    return {
      textTransform: 'none',
      property: 'none',
    };
  }

  // Certain types of connected Variables may *also* require a specific ID in the corresponding DealConnection,
  // in addition to the DealConnection itself
  // Currently this is limited to Filevine's collection items, but could soon be expanded to other services (e.g., Salesforce products)
  // So we're attempting here to build the foundation for a generalized "data source" (DS) browsing/targeting model
  get requiredDS() {
    if (this.type !== VariableType.CONNECTED) return null;
    // Note: expand this array when necessary to include additional types which require browsing/ID
    if (
      ![EXTERNAL_TYPES.COLLECTION_FIELD].includes(this.externalType) &&
      ![EXTERNAL_TYPES.COLLECTION_FIELD].includes(this.projectLinkFieldType)
    )
      return null;
    if (!this.externalSelector) return null;
    // Filevine externalSelector for collection items take the form of {collection}.{field}, e.g., expenses.amount
    // So this will yield "expenses" -- which is the required key in DealConnection.idFields
    return this.externalSelector.split('.')[0];
  }

  // If this connected Variable has a requiredDS, this is a convenience getter to navigate the object model,
  // and pull out the correct stored data source id from the corresponding DealConnection
  get connectedDSID() {
    const connection = find(this.deal.connections, {
      type: this.connectType,
    });
    const requiredDS = this.requiredDS;
    if (!connection || !requiredDS) return null;
    return get(connection, `idFields.${requiredDS}`);
  }

  // Another convenience getter so that we know when to automatically trigger the DataSourceBrowser
  get isMissingDS() {
    if (this.type !== VariableType.CONNECTED) return false;
    const connection = find(this.deal.connections, {
      type: this.connectType,
    });

    if (connection && this.requiredDS && !this.connectedDSID) return true;
    return false;
  }

  get isBroken() {
    if (this.type === VariableType.REF) return !this.val;
    if (this.type === VariableType.CONNECTED) return !this.baseVariable.connection && !this.deal?.preview;
    if (this.type === VariableType.CALCULATED) {
      try {
        this.calculate(this.formula);
      } catch (err) {
        return true;
      }
      return false;
    }

    // For tableTotal vars, simply accessing the relatedTotalColumn getter will determine whether it's broken or not
    if (this.isTableTotal) {
      try {
        if (this.relatedTotalColumn) return false;
      } catch (err) {
        return true;
      }
    }

    return false;
  }

  get isProperty() {
    const [, property] = this.name.toString().split('.');
    return !!property;
  }

  get multilineValueLabelsMap() {
    if (Array.isArray(this.multilineValueLabels)) {
      return this.multilineValueLabels?.reduce((acc, valLabel) => {
        if (Array.isArray(valLabel) && valLabel.length >= 2) {
          const [val, label] = valLabel;
          return { ...acc, [val]: label };
        }
        return acc;
      }, {});
    } else {
      return this.multilineValueLabels;
    }
  }

  get json() {
    return {
      name: this.name,
      type: this.type,
      valueType: this.valueType || null,
      value: this.value || null,
      prompt: this.prompt || null,
      displayName: this.displayName || null,
      assigned: this.assigned || null,
      multiline: this.multiline || null,
      multilineValueOptions: this.multilineValueOptions || null,
      multilineValueLabels: this.multilineValueLabels || [],

      connectType: this.connectType || null,
      externalObjectID: this.externalObjectID || null,
      externalType: this.externalType || null,
      externalSelector: this.externalSelector || null,
      autoPull: get(this, 'autoPull', false),
      autoPush: get(this, 'autoPush', false),
      writable: get(this, 'writable', false),
      multiline: get(this, 'multiline', false),
      projectLinkFieldType: this.projectLinkFieldType || null,
      columns:
        this.valueType === ValueType.TABLE && get(this, '_columns.length') > 0 ? map(this._columns, 'json') : null,
      showTotalRow: this.showTotalRow || false,
      totalRowLabel: this.totalRowLabel,
      hasAttachment: this.hasAttachment,
      variants: this.variants.length > 0 ? this.variants.join('|') : null,
      extract: this.extract,
      extractType: this.extractType,
      extractionInstructions: this.extractionInstructions,
    };
  }

  //This change converts the stringified json used to save table column headers correctly back into an array
  get tableValueFormatted() {
    //we need try catch because this specifically checks if the value is a valid json string to be parsed.
    try {
      const json = JSON.parse(this.value);
      return json;
    } catch (err) {
      //legacy
      return this.value || [];
    }
  }

  //Most variables have their valueType explicitly specified in the stored json,
  //but calculated variables' valueType is a function its formula, so needs to be dynamically determined on the fly
  get valueType() {
    if (this.type === VariableType.CALCULATED) {
      try {
        const { valueType } = this.calculate(this.formula);
        return valueType;
      } catch (err) {
        return ValueType.NUMBER;
      }
    } else {
      return this._valueType;
    }
  }

  set valueType(vt) {
    this._valueType = vt;
  }
}
